import { useState } from 'react';
import { 
  FaComments, 
  FaBullhorn, 
  FaVideo, 
  FaSearch, 
  FaPlus, 
  FaUserFriends, 
  FaRegClock,
  FaRegCalendarAlt,
  FaRegStar,
  FaRegBookmark,
  FaRegBell,
  FaChevronDown,
  FaEllipsisH,
  FaPaperPlane,
  FaRegSmile,
  FaPaperclip,
  FaUserGraduate,
  FaUserTie
} from 'react-icons/fa';

const CommunicationHub = () => {
  const [activeTab, setActiveTab] = useState('forums');
  const [activeForumCategory, setActiveForumCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewTopicForm, setShowNewTopicForm] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  
  // Sample data for demonstration
  const forumCategories = [
    { id: 'all', name: 'All Topics' },
    { id: 'course', name: 'Course Discussions' },
    { id: 'general', name: 'General' },
    { id: 'technical', name: 'Technical Help' },
    { id: 'career', name: 'Career Advice' },
    { id: 'events', name: 'Events' },
  ];
  
  const forumTopics = [
    { 
      id: 1, 
      title: 'Tips for React Performance Optimization', 
      category: 'course',
      author: { name: 'Alex Johnson', avatar: null, role: 'student' },
      replies: 24,
      views: 156,
      lastActivity: '2023-11-10T14:30:00',
      pinned: true,
      tags: ['react', 'performance', 'frontend']
    },
    { 
      id: 2, 
      title: 'Introduction Thread - November Cohort', 
      category: 'general',
      author: { name: 'Maria Garcia', avatar: null, role: 'instructor' },
      replies: 42,
      views: 210,
      lastActivity: '2023-11-09T10:15:00',
      pinned: true,
      tags: ['introductions', 'community']
    },
    { 
      id: 3, 
      title: 'Struggling with Node.js Async Functions', 
      category: 'technical',
      author: { name: 'David Kim', avatar: null, role: 'student' },
      replies: 18,
      views: 97,
      lastActivity: '2023-11-10T09:45:00',
      pinned: false,
      tags: ['nodejs', 'javascript', 'async']
    },
    { 
      id: 4, 
      title: 'Career Transition from Marketing to UX Design', 
      category: 'career',
      author: { name: 'Sarah Williams', avatar: null, role: 'student' },
      replies: 31,
      views: 183,
      lastActivity: '2023-11-08T16:20:00',
      pinned: false,
      tags: ['career-change', 'ux-design', 'portfolio']
    },
    { 
      id: 5, 
      title: 'Virtual Tech Conference - December 15th', 
      category: 'events',
      author: { name: 'Admin', avatar: null, role: 'admin' },
      replies: 12,
      views: 245,
      lastActivity: '2023-11-07T11:30:00',
      pinned: true,
      tags: ['conference', 'networking', 'professional-development']
    },
    { 
      id: 6, 
      title: 'Best Practices for Database Normalization', 
      category: 'course',
      author: { name: 'Emma Davis', avatar: null, role: 'instructor' },
      replies: 15,
      views: 112,
      lastActivity: '2023-11-09T13:45:00',
      pinned: false,
      tags: ['database', 'sql', 'backend']
    },
  ];
  
  const mentors = [
    { 
      id: 1, 
      name: 'Dr. James Wilson', 
      avatar: null,
      expertise: ['Machine Learning', 'Python', 'Data Science'],
      rating: 4.9,
      reviews: 124,
      availability: 'Available',
      bio: 'Senior Data Scientist with 10+ years of industry experience. PhD in Computer Science from MIT.'
    },
    { 
      id: 2, 
      name: 'Prof. Olivia Martinez', 
      avatar: null,
      expertise: ['Web Development', 'JavaScript', 'React'],
      rating: 4.8,
      reviews: 98,
      availability: 'Busy',
      bio: 'Full-stack developer and educator. Author of "Modern Web Development" and conference speaker.'
    },
    { 
      id: 3, 
      name: 'Noah Thompson', 
      avatar: null,
      expertise: ['UX/UI Design', 'User Research', 'Figma'],
      rating: 4.7,
      reviews: 86,
      availability: 'Available',
      bio: 'Lead UX Designer at DesignCo. Previously at Google and Adobe. Passionate about accessible design.'
    },
    { 
      id: 4, 
      name: 'Sophia Lee', 
      avatar: null,
      expertise: ['Mobile Development', 'React Native', 'Flutter'],
      rating: 4.9,
      reviews: 112,
      availability: 'Available',
      bio: 'Mobile app developer with 50+ published apps. Specializes in cross-platform development.'
    },
  ];
  
  const announcements = [
    {
      id: 1,
      title: 'Platform Maintenance - November 15th',
      content: 'The platform will be down for scheduled maintenance from 2:00 AM to 4:00 AM EST on November 15th. Please plan accordingly.',
      author: { name: 'System Admin', role: 'admin' },
      date: '2023-11-08T09:00:00',
      priority: 'high',
      read: true
    },
    {
      id: 2,
      title: 'New Course Release: Advanced Machine Learning',
      content: 'We\'re excited to announce our new course on Advanced Machine Learning techniques. Early enrollment opens next week with a 20% discount.',
      author: { name: 'Course Team', role: 'admin' },
      date: '2023-11-09T11:30:00',
      priority: 'medium',
      read: false
    },
    {
      id: 3,
      title: 'Updated Assignment Guidelines',
      content: 'We\'ve updated our assignment submission guidelines. Please review the changes before your next submission.',
      author: { name: 'Dr. Emma Davis', role: 'instructor' },
      date: '2023-11-10T14:15:00',
      priority: 'medium',
      read: false
    },
    {
      id: 4,
      title: 'Congratulations to Hackathon Winners!',
      content: 'Congratulations to Team Innovate for winning our Fall Hackathon! View all projects and winners on the Events page.',
      author: { name: 'Events Committee', role: 'admin' },
      date: '2023-11-07T16:45:00',
      priority: 'low',
      read: true
    },
  ];
  
  const studyGroups = [
    {
      id: 1,
      name: 'React Study Group',
      description: 'Weekly sessions to practice React concepts and work on projects together',
      members: 12,
      nextSession: '2023-11-15T18:00:00',
      topics: ['React', 'Redux', 'Hooks'],
      joinStatus: 'joined'
    },
    {
      id: 2,
      name: 'Algorithm Practice',
      description: 'Daily algorithm challenges and discussion of solutions',
      members: 24,
      nextSession: '2023-11-12T17:00:00',
      topics: ['Algorithms', 'Data Structures', 'Problem Solving'],
      joinStatus: 'joined'
    },
    {
      id: 3,
      name: 'UX Design Portfolio Review',
      description: 'Bi-weekly portfolio reviews and feedback sessions',
      members: 8,
      nextSession: '2023-11-20T19:00:00',
      topics: ['UX Design', 'Portfolio', 'Career'],
      joinStatus: 'open'
    },
    {
      id: 4,
      name: 'Database Design Masterclass',
      description: 'Advanced database design concepts and case studies',
      members: 15,
      nextSession: '2023-11-14T20:00:00',
      topics: ['Databases', 'SQL', 'NoSQL', 'Data Modeling'],
      joinStatus: 'open'
    },
  ];
  
  const chats = [
    {
      id: 1,
      with: { name: 'Alex Johnson', avatar: null, status: 'online', lastActive: null },
      lastMessage: { text: 'Thanks for the help with that React component!', time: '2023-11-10T15:30:00', read: true, sender: 'them' },
      unread: 0
    },
    {
      id: 2,
      with: { name: 'Maria Garcia', avatar: null, status: 'offline', lastActive: '2023-11-10T14:45:00' },
      lastMessage: { text: 'Could you review my assignment when you have time?', time: '2023-11-10T14:45:00', read: false, sender: 'them' },
      unread: 1
    },
    {
      id: 3,
      with: { name: 'React Study Group', avatar: null, status: 'group', members: 12 },
      lastMessage: { text: 'Don\'t forget our session tomorrow at 6 PM!', time: '2023-11-09T20:15:00', read: true, sender: 'James Wilson' },
      unread: 0
    },
    {
      id: 4,
      with: { name: 'Dr. James Wilson', avatar: null, status: 'online', lastActive: null, role: 'mentor' },
      lastMessage: { text: 'I\'ve reviewed your project. Let\'s discuss the feedback tomorrow.', time: '2023-11-10T11:20:00', read: true, sender: 'them' },
      unread: 0
    },
  ];
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
  };
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return date.toLocaleDateString(undefined, options);
  };

  // Filter forum topics based on active category and search query
  const filteredTopics = forumTopics.filter(topic => {
    const matchesCategory = activeForumCategory === 'all' || topic.category === activeForumCategory;
    const matchesSearch = searchQuery === '' || 
      topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-white container-rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b">
        <h2 className="text-lg font-medium">Communication Hub</h2>
        <p className="text-sm text-gray-500 mt-1">Connect, collaborate, and communicate with peers and instructors</p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('forums')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'forums' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaComments className="inline mr-2" />
            Discussion Forums
          </button>
          <button
            onClick={() => setActiveTab('mentoring')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'mentoring' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaUserFriends className="inline mr-2" />
            Peer Mentoring
          </button>
          <button
            onClick={() => setActiveTab('announcements')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'announcements' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaBullhorn className="inline mr-2" />
            Announcements
          </button>
          <button
            onClick={() => setActiveTab('studygroups')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'studygroups' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaVideo className="inline mr-2" />
            Study Groups
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Discussion Forums */}
        {activeTab === 'forums' && (
          <div>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <select 
                    className="appearance-none bg-gray-100 border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={activeForumCategory}
                    onChange={(e) => setActiveForumCategory(e.target.value)}
                  >
                    {forumCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <FaChevronDown className="h-4 w-4" />
                  </div>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search discussions..."
                    className="bg-gray-100 border border-gray-300 rounded-md py-2 pl-10 pr-4 text-sm w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              <button 
                className="bg-primary text-white rounded-md px-4 py-2 text-sm font-medium flex items-center justify-center hover:bg-primary-dark transition-colors"
                onClick={() => setShowNewTopicForm(!showNewTopicForm)}
              >
                <FaPlus className="mr-2" />
                New Topic
              </button>
            </div>
            
            {showNewTopicForm && (
              <div className="bg-gray-50 container-rounded-lg p-4 mb-6">
                <h3 className="text-md font-medium mb-4">Create New Topic</h3>
                <form>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input 
                      type="text" 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" 
                      placeholder="Enter topic title"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary">
                      {forumCategories.filter(c => c.id !== 'all').map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-32" 
                      placeholder="Write your post here..."
                    ></textarea>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                    <input 
                      type="text" 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" 
                      placeholder="e.g. javascript, react, help"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button 
                      type="button" 
                      className="border border-gray-300 text-gray-700 rounded-md px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
                      onClick={() => setShowNewTopicForm(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors"
                    >
                      Create Topic
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Author</th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Replies</th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Views</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTopics.map((topic) => (
                    <tr key={topic.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-start">
                          <div className="ml-0">
                            <div className="flex items-center">
                              {topic.pinned && (
                                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded mr-2">Pinned</span>
                              )}
                              <div className="text-sm font-medium text-gray-900 hover:text-primary">{topic.title}</div>
                            </div>
                            <div className="flex flex-wrap mt-1">
                              {topic.tags.map((tag, tagIndex) => (
                                <span key={tagIndex} className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 mr-1 mb-1">#{tag}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                            {topic.author.avatar ? (
                              <img src={topic.author.avatar} alt={topic.author.name} className="h-8 w-8 rounded-full" />
                            ) : (
                              topic.author.name.charAt(0)
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{topic.author.name}</div>
                            <div className="text-xs text-gray-500">
                              {topic.author.role === 'instructor' ? (
                                <span className="flex items-center">
                                  <FaUserTie className="mr-1 text-blue-500" /> Instructor
                                </span>
                              ) : topic.author.role === 'student' ? (
                                <span className="flex items-center">
                                  <FaUserGraduate className="mr-1 text-green-500" /> Student
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <FaRegBell className="mr-1 text-purple-500" /> Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center hidden md:table-cell">
                        <span className="text-sm text-gray-900">{topic.replies}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center hidden lg:table-cell">
                        <span className="text-sm text-gray-500">{topic.views}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {formatDate(topic.lastActivity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredTopics.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No topics found. Try adjusting your search or category filter.</p>
              </div>
            )}
          </div>
        )}

        {/* Peer Mentoring */}
        {activeTab === 'mentoring' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Available Mentors</h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by expertise..."
                  className="bg-gray-100 border border-gray-300 rounded-md py-2 pl-10 pr-4 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mentors.map(mentor => (
                <div key={mentor.id} className="border container-rounded-lg overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-lg font-medium">
                          {mentor.avatar ? (
                            <img src={mentor.avatar} alt={mentor.name} className="h-12 w-12 rounded-full" />
                          ) : (
                            mentor.name.charAt(0)
                          )}
                        </div>
                        <div className="ml-4">
                          <h4 className="text-lg font-medium">{mentor.name}</h4>
                          <div className="flex items-center mt-1">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <FaRegStar 
                                  key={i} 
                                  className={`h-4 w-4 ${i < Math.floor(mentor.rating) ? 'text-yellow-400' : 'text-gray-300'}`} 
                                />
                              ))}
                            </div>
                            <span className="text-sm text-gray-600 ml-2">{mentor.rating} ({mentor.reviews})</span>
                          </div>
                        </div>
                      </div>
                      <span 
                        className={`text-xs px-2 py-1 rounded-full ${mentor.availability === 'Available' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'}`}
                      >
                        {mentor.availability}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-4">{mentor.bio}</p>
                    
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Expertise</h5>
                      <div className="flex flex-wrap">
                        {mentor.expertise.map((skill, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2 mb-2">{skill}</span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mt-6 flex space-x-3">
                      <button className="flex-1 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors">
                        Schedule Session
                      </button>
                      <button className="flex-1 border border-gray-300 text-gray-700 rounded-md px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors">
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8">
              <h3 className="font-medium mb-4">Become a Peer Mentor</h3>
              <div className="bg-blue-50 border border-blue-200 container-rounded-lg p-6">
                <h4 className="text-lg font-medium text-blue-800 mb-2">Share Your Knowledge</h4>
                <p className="text-sm text-blue-700 mb-4">
                  Help fellow students while reinforcing your own understanding. Peer mentoring is a rewarding way to contribute to our learning community.
                </p>
                <ul className="space-y-2 text-sm text-blue-700 mb-6">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">
                      1
                    </span>
                    <span>Complete at least 3 courses with a grade of 85% or higher</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">
                      2
                    </span>
                    <span>Submit an application highlighting your areas of expertise</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">
                      3
                    </span>
                    <span>Complete a brief mentor training session</span>
                  </li>
                </ul>
                <button className="bg-blue-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-blue-700 transition-colors">
                  Apply to Become a Mentor
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Announcements */}
        {activeTab === 'announcements' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Announcement Center</h3>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  {announcements.filter(a => !a.read).length} unread
                </span>
                <button className="text-primary text-sm hover:underline">
                  Mark all as read
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {announcements.map(announcement => (
                <div 
                  key={announcement.id} 
                  className={`border container-rounded-lg overflow-hidden ${!announcement.read ? 'border-l-4 border-l-primary' : ''}`}
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          {announcement.priority === 'high' && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded mr-2">Important</span>
                          )}
                          <h4 className="text-md font-medium">{announcement.title}</h4>
                        </div>
                        <div className="flex items-center mt-1 text-sm text-gray-500">
                          <span>From: {announcement.author.name}</span>
                          <span className="mx-2">•</span>
                          <span>{formatDate(announcement.date)}</span>
                        </div>
                      </div>
                      <button className="text-gray-400 hover:text-gray-500">
                        <FaEllipsisH />
                      </button>
                    </div>
                    
                    <div className="mt-3">
                      <p className="text-sm text-gray-700">{announcement.content}</p>
                    </div>
                    
                    <div className="mt-4 flex justify-end space-x-3">
                      {!announcement.read && (
                        <button className="text-primary text-sm hover:underline">
                          Mark as read
                        </button>
                      )}
                      <button className="text-primary text-sm hover:underline">
                        View details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Study Groups */}
        {activeTab === 'studygroups' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Virtual Study Groups</h3>
              <button className="bg-primary text-white rounded-md px-4 py-2 text-sm font-medium flex items-center justify-center hover:bg-primary-dark transition-colors">
                <FaPlus className="mr-2" />
                Create Group
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {studyGroups.map(group => (
                <div key={group.id} className="border container-rounded-lg overflow-hidden">
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <h4 className="text-lg font-medium">{group.name}</h4>
                      <span className="text-sm text-gray-500">{group.members} members</span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-2">{group.description}</p>
                    
                    <div className="mt-4">
                      <div className="flex items-center text-sm">
                        <FaRegCalendarAlt className="text-gray-400 mr-2" />
                        <span className="text-gray-700">Next session: {formatDateTime(group.nextSession)}</span>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Topics</h5>
                      <div className="flex flex-wrap">
                        {group.topics.map((topic, index) => (
                          <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded mr-2 mb-2">{topic}</span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      {group.joinStatus === 'joined' ? (
                        <button className="w-full bg-green-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-green-700 transition-colors">
                          Join Next Session
                        </button>
                      ) : (
                        <button className="w-full bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors">
                          Join Group
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8">
              <h3 className="font-medium mb-4">Upcoming Sessions</h3>
              <div className="bg-gray-50 container-rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {studyGroups
                      .filter(group => group.joinStatus === 'joined')
                      .map(group => (
                        <tr key={group.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{group.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{formatDateTime(group.nextSession)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{group.topics[0]}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button className="text-primary hover:text-primary-dark">Join</button>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationHub;